import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Grid,
    IconButton,
    Link as MuiLink,
    Paper,
    Skeleton,
    Stack,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ContentCopy,
    EventRounded,
    Facebook,
    Favorite,
    FavoriteBorder,
    GroupsRounded,
    Instagram,
    Language,
    LinkedIn,
    X,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useNotifications } from '@toolpad/core';
import { useApiClient } from '../../ApiClientProvider';
import AnimatedGradient from '../../components/AnimatedGradient';
import { participantContentSx } from '../../components/ParticipantPage';
import { CommunityCard } from '../../components/entities/communities/CommunityCard';
import EventCard from '../../components/entities/events/EventCard';
import { ICommunity } from '../../interfaces/ICommunityHub';
import { EventStatus } from '../../enums/EventStatus';
import { IEventList } from '../../interfaces/IEventList';
import { IPublicOrganiserCommunity } from '../../interfaces/IPublicOrganiserCommunity';
import { IPublicOrganiserEventFeedItem, IPublicOrganiserProfile } from '../../interfaces/IPublicOrganiserProfile';
import { paths, buildPath } from '../../routes/paths';
import { ParticipantCommunityHubService } from '../../services/participantCommunityHubService';
import { PublicEventService } from '../../services/publicEventService';
import { useSession } from '../../SessionContext';
import { logEvent } from '../../utils/analytics';
import { bumpScoreCheckVersion } from '../../redux/scoreSlice';
import PageMetadata from '../../components/SEO/PageMetadata';
import OrganizationSchema from '../../components/SEO/OrganizationSchema';

type SocialLink = {
    label: string;
    href: string;
    icon: React.ReactElement;
};

const ORGANISER_EVENTS_PAGE_SIZE = 8;
const ORGANISER_COMMUNITIES_PAGE_SIZE = 6;

type OrganiserProfileTab = 'about' | 'communities' | 'upcoming' | 'past';

const appendUniqueEvents = <T extends { eventId: string }>(existing: T[], incoming: T[]) => {
    const seenIds = new Set(existing.map((eventItem) => eventItem.eventId));
    const nextItems = incoming.filter((eventItem) => !seenIds.has(eventItem.eventId));
    return nextItems.length === 0 ? existing : [...existing, ...nextItems];
};

const appendUniqueById = <T extends { id: string }>(existing: T[], incoming: T[]) => {
    const seenIds = new Set(existing.map((item) => item.id));
    const nextItems = incoming.filter((item) => !seenIds.has(item.id));
    return nextItems.length === 0 ? existing : [...existing, ...nextItems];
};

const getWebsiteHost = (website?: string) => {
    if (!website) {
        return null;
    }

    try {
        const url = new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`);
        return url.hostname.replace(/^www\./, '');
    } catch {
        return website;
    }
};

const getExternalUrl = (website?: string) =>
    website ? (/^https?:\/\//i.test(website) ? website : `https://${website}`) : undefined;

/** Adapts the organiser feed's slimmer event shape onto IEventList so it can render through the shared EventCard (variant="row"). */
const toEventRowItem = (eventItem: IPublicOrganiserEventFeedItem, isPast: boolean): IEventList => ({
    id: eventItem.eventId,
    title: eventItem.title,
    startDate: eventItem.startDate,
    endDate: eventItem.endDate,
    imageUrl: eventItem.imageUrl || '',
    price: eventItem.fromPrice ?? 0,
    location: eventItem.locationCities?.[0] || '',
    locationCity: eventItem.locationCities,
    isWaitlisted: false,
    isInviteOnly: false,
    isEventPublic: true,
    canRegister: !isPast,
    description: '',
    tags: [],
    registrationDeadline: eventItem.endDate,
    status: isPast ? EventStatus.Completed : EventStatus.Scheduled,
    attendees: eventItem.registrationCount ?? 0,
    coordinators: [],
    taskCounts: [],
});

/** Adapts the organiser feed's slimmer community shape onto ICommunity so it can render through the shared CommunityCard. */
const toCommunityCardShape = (community: IPublicOrganiserCommunity, organiserName?: string): ICommunity => ({
    id: community.id,
    ownerCompanyId: '',
    name: community.name,
    slug: community.slug,
    description: community.description,
    imageUrl: community.imageUrl ?? undefined,
    logoUrl: community.logoUrl ?? undefined,
    imageFocalPointX: community.imageFocalPointX ?? 50,
    imageFocalPointY: community.imageFocalPointY ?? 50,
    visibility: community.visibility === 'Private' || community.visibility === 'Unlisted' ? community.visibility : 'Public',
    allowAttendeePosts: true,
    isArchived: false,
    createdByUserId: '',
    memberCount: community.memberCount,
    createdAt: new Date().toISOString(),
    isMember: community.isMember,
    isJoinable: community.isJoinable,
    ownerCompanyName: organiserName,
});

const OrganiserProfilePage: React.FC = () => {
    const { companyKey } = useParams<{ companyKey: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const apiClient = useApiClient('applicationClient');
    const { session } = useSession();
    const isLoggedIn = !!session;
    const notifications = useNotifications();
    const theme = useTheme();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const eventService = useMemo(() => new PublicEventService(apiClient, 'event'), [apiClient]);
    const participantCommunityHubService = useMemo(() => new ParticipantCommunityHubService(apiClient), [apiClient]);

    const [profile, setProfile] = useState<IPublicOrganiserProfile | null>(null);
    const [hostedCommunities, setHostedCommunities] = useState<IPublicOrganiserCommunity[]>([]);
    const [participatingCommunities, setParticipatingCommunities] = useState<IPublicOrganiserCommunity[]>([]);
    const [communityCounts, setCommunityCounts] = useState({ hosted: 0, participating: 0 });
    const [communitiesLoading, setCommunitiesLoading] = useState(true);
    const [communitiesLoaded, setCommunitiesLoaded] = useState(false);
    const [nextHostedPage, setNextHostedPage] = useState(2);
    const [nextParticipatingPage, setNextParticipatingPage] = useState(2);
    const [loadingMoreCommunities, setLoadingMoreCommunities] = useState(false);
    const [upcomingEvents, setUpcomingEvents] = useState<IPublicOrganiserEventFeedItem[]>([]);
    const [pastEvents, setPastEvents] = useState<IPublicOrganiserEventFeedItem[]>([]);
    const [upcomingTotalCount, setUpcomingTotalCount] = useState(0);
    const [pastTotalCount, setPastTotalCount] = useState(0);
    const [nextUpcomingPage, setNextUpcomingPage] = useState(2);
    const [nextPastPage, setNextPastPage] = useState(2);
    const [upcomingLoaded, setUpcomingLoaded] = useState(false);
    const [pastLoaded, setPastLoaded] = useState(false);
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [pastLoading, setPastLoading] = useState(false);
    const [loadingMoreEvents, setLoadingMoreEvents] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFollowing, setIsFollowing] = useState<boolean>(false);
    const [followLoading, setFollowLoading] = useState(false);
    const [joiningCommunityId, setJoiningCommunityId] = useState<string | null>(null);
    const requestedTab = searchParams.get('tab');
    const activeTab: OrganiserProfileTab = requestedTab === 'communities' || requestedTab === 'upcoming' || requestedTab === 'past'
        ? requestedTab
        : 'about';
    const setActiveTab = (tab: OrganiserProfileTab) => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (tab === 'about') {
                next.delete('tab');
            } else {
                next.set('tab', tab);
            }
            return next;
        });
    };
    const [sessionId] = useState<string>(() => {
        const storageKey = 'organiser-profile-analytics-session-id';
        const existing = window.localStorage.getItem(storageKey);
        if (existing) {
            return existing;
        }

        const created = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        window.localStorage.setItem(storageKey, created);
        return created;
    });

    const utm = useMemo(() => {
        const query = new URLSearchParams(window.location.search);
        return {
            utmSource: query.get('utm_source') || undefined,
            utmMedium: query.get('utm_medium') || undefined,
            utmCampaign: query.get('utm_campaign') || undefined,
        };
    }, []);

    const trackInteraction = (interactionType: string, interactionLabel?: string, relatedEventId?: string) => {
        if (!companyKey) {
            return;
        }

        void eventService.trackOrganiserProfileInteraction(companyKey, {
            interactionType,
            interactionLabel,
            relatedEventId,
            sessionId,
            userAgent: navigator.userAgent,
            referrer: document.referrer || undefined,
            utmSource: utm.utmSource,
            utmMedium: utm.utmMedium,
            utmCampaign: utm.utmCampaign,
        });
    };

    const hasMoreUpcomingEvents = upcomingEvents.length < upcomingTotalCount;
    const hasMorePastEvents = pastEvents.length < pastTotalCount;
    const hasMoreEvents = activeTab === 'upcoming'
        ? hasMoreUpcomingEvents
        : activeTab === 'past'
            ? hasMorePastEvents
            : false;
    const hasMoreHostedCommunities = hostedCommunities.length < communityCounts.hosted;
    const hasMoreParticipatingCommunities = participatingCommunities.length < communityCounts.participating;
    const hasMoreCommunities = hasMoreHostedCommunities || hasMoreParticipatingCommunities;
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const communitiesLoadMoreRef = useRef<HTMLDivElement | null>(null);

    const loadUpcomingEventsPage = useCallback(async (page: number, append: boolean) => {
        if (!companyKey) {
            return;
        }

        const response = await eventService.getOrganiserEvents(companyKey, 'Upcoming', page, ORGANISER_EVENTS_PAGE_SIZE);
        setUpcomingEvents((current) => append ? appendUniqueEvents(current, response.items) : response.items);
        setUpcomingTotalCount(response.totalCount);
        setNextUpcomingPage(response.page + 1);
        setUpcomingLoaded(true);
    }, [companyKey, eventService]);

    const loadPastEventsPage = useCallback(async (page: number, append: boolean) => {
        if (!companyKey) {
            return;
        }

        const response = await eventService.getOrganiserEvents(companyKey, 'Past', page, ORGANISER_EVENTS_PAGE_SIZE);
        setPastEvents((current) => append ? appendUniqueEvents(current, response.items) : response.items);
        setPastTotalCount(response.totalCount);
        setNextPastPage(response.page + 1);
        setPastLoaded(true);
    }, [companyKey, eventService]);

    const loadMoreEvents = useCallback(async () => {
        if (!companyKey || loadingMoreEvents) {
            return;
        }

        const request = activeTab === 'upcoming' && hasMoreUpcomingEvents
            ? loadUpcomingEventsPage(nextUpcomingPage, true)
            : activeTab === 'past' && hasMorePastEvents
                ? loadPastEventsPage(nextPastPage, true)
                : null;

        if (!request) {
            return;
        }

        setLoadingMoreEvents(true);

        try {
            await request;
        } catch {
            notifications.show('Unable to load more events right now.', {
                severity: 'error',
                autoHideDuration: 2500,
            });
        } finally {
            setLoadingMoreEvents(false);
        }
    }, [
        companyKey,
        eventService,
        hasMorePastEvents,
        hasMoreUpcomingEvents,
        activeTab,
        loadPastEventsPage,
        loadUpcomingEventsPage,
        loadingMoreEvents,
        nextPastPage,
        nextUpcomingPage,
        notifications,
    ]);

    const loadMoreCommunities = useCallback(async () => {
        if (!companyKey || loadingMoreCommunities) {
            return;
        }

        try {
            setLoadingMoreCommunities(true);

            if (hasMoreHostedCommunities) {
                const response = await eventService.getOrganiserCommunities(companyKey, {
                    relationship: 'Hosted',
                    page: nextHostedPage,
                    pageSize: ORGANISER_COMMUNITIES_PAGE_SIZE,
                });
                setHostedCommunities((current) => appendUniqueById(current, response.communities));
                setCommunityCounts((current) => ({ ...current, hosted: response.totalCount }));
                setNextHostedPage((page) => page + 1);
            } else if (hasMoreParticipatingCommunities) {
                const response = await eventService.getOrganiserCommunities(companyKey, {
                    relationship: 'Participating',
                    page: nextParticipatingPage,
                    pageSize: ORGANISER_COMMUNITIES_PAGE_SIZE,
                });
                setParticipatingCommunities((current) => appendUniqueById(current, response.communities));
                setCommunityCounts((current) => ({ ...current, participating: response.totalCount }));
                setNextParticipatingPage((page) => page + 1);
            }
        } catch {
            notifications.show('Unable to load more communities right now.', {
                severity: 'error',
                autoHideDuration: 2500,
            });
        } finally {
            setLoadingMoreCommunities(false);
        }
    }, [
        companyKey,
        eventService,
        hasMoreHostedCommunities,
        hasMoreParticipatingCommunities,
        loadingMoreCommunities,
        nextHostedPage,
        nextParticipatingPage,
        notifications,
    ]);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!companyKey) {
                setError('Organiser details were not provided.');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const profileResponse = await eventService.getOrganiserProfile(companyKey, 3);

                setProfile(profileResponse);
                setIsFollowing(profileResponse.isFollowed ?? false);
            } catch (err: any) {
                setProfile(null);
                setUpcomingEvents([]);
                setPastEvents([]);
                setUpcomingTotalCount(0);
                setPastTotalCount(0);
                setUpcomingLoaded(false);
                setPastLoaded(false);
                setError(err?.message || 'Unable to load organiser profile right now.');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [companyKey, eventService]);

    useEffect(() => {
        if (activeTab !== 'upcoming' || upcomingLoaded || upcomingLoading || !companyKey) {
            return;
        }

        setUpcomingLoading(true);
        void loadUpcomingEventsPage(1, false)
            .catch(() => {
                notifications.show('Unable to load upcoming events right now.', {
                    severity: 'error',
                    autoHideDuration: 2500,
                });
            })
            .finally(() => {
                setUpcomingLoading(false);
            });
    }, [activeTab, companyKey, loadUpcomingEventsPage, notifications, upcomingLoaded, upcomingLoading]);

    useEffect(() => {
        if (activeTab !== 'past' || pastLoaded || pastLoading || !companyKey) {
            return;
        }

        setPastLoading(true);
        void loadPastEventsPage(1, false)
            .catch(() => {
                notifications.show('Unable to load past events right now.', {
                    severity: 'error',
                    autoHideDuration: 2500,
                });
            })
            .finally(() => {
                setPastLoading(false);
            });
    }, [activeTab, companyKey, loadPastEventsPage, notifications, pastLoaded, pastLoading]);

    useEffect(() => {
        if (!companyKey || !profile?.companyKey || profile.companyKey === companyKey) {
            return;
        }

        navigate(buildPath(paths.public.eventOrganizerProfile, { companyKey: profile.companyKey }) + location.search, { replace: true });
    }, [companyKey, profile?.companyKey, navigate, location.search]);

    useEffect(() => {
        if (communitiesLoaded || !companyKey) {
            return;
        }

        const fetchCommunities = async () => {
            try {
                setCommunitiesLoading(true);
                const [hostedResponse, participatingResponse] = await Promise.all([
                    eventService.getOrganiserCommunities(companyKey, { relationship: 'Hosted', page: 1, pageSize: ORGANISER_COMMUNITIES_PAGE_SIZE }),
                    eventService.getOrganiserCommunities(companyKey, { relationship: 'Participating', page: 1, pageSize: ORGANISER_COMMUNITIES_PAGE_SIZE }),
                ]);

                setHostedCommunities(hostedResponse.communities);
                setParticipatingCommunities(participatingResponse.communities);
                setCommunityCounts({
                    hosted: hostedResponse.totalCount,
                    participating: participatingResponse.totalCount,
                });
                setCommunitiesLoaded(true);
            } catch {
                setHostedCommunities([]);
                setParticipatingCommunities([]);
                setCommunityCounts({ hosted: 0, participating: 0 });
                setCommunitiesLoaded(true);
            } finally {
                setCommunitiesLoading(false);
            }
        };

        void fetchCommunities();
    }, [communitiesLoaded, companyKey, eventService]);

    // Track page view analytics
    useEffect(() => {
        if (!profile) {
            return;
        }

        logEvent('Organiser Profile Page', 'view', profile.companyName);
        trackInteraction('ProfileView', profile.companyName);
    }, [profile]);

    useEffect(() => {
        if (!loadMoreRef.current || !hasMoreEvents || loading) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    void loadMoreEvents();
                }
            },
            { rootMargin: '240px 0px' },
        );

        observer.observe(loadMoreRef.current);

        return () => {
            observer.disconnect();
        };
    }, [hasMoreEvents, loadMoreEvents, loading]);

    useEffect(() => {
        if (activeTab !== 'communities' || !communitiesLoadMoreRef.current || !hasMoreCommunities || communitiesLoading) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    void loadMoreCommunities();
                }
            },
            { rootMargin: '240px 0px' },
        );

        observer.observe(communitiesLoadMoreRef.current);

        return () => {
            observer.disconnect();
        };
    }, [activeTab, communitiesLoading, hasMoreCommunities, loadMoreCommunities]);

    const profileUrl = `${window.location.origin}${buildPath(paths.public.eventOrganizerProfile, { companyKey: profile?.companyKey || companyKey || '' })}`;

    const handleCopyProfileLink = async () => {
        try {
            await navigator.clipboard.writeText(profileUrl);
            notifications.show('Profile link copied to clipboard', { severity: 'success', autoHideDuration: 2200 });
            logEvent('Organiser Profile Page', 'click', 'Copy Profile Link');
            trackInteraction('CopyProfileLink', 'Copy Profile Link');
        } catch {
            notifications.show('Unable to copy profile link', { severity: 'error', autoHideDuration: 2500 });
        }
    };

    const redirectToSignIn = () => {
        const returnUrl = `${location.pathname}${location.search}`;
        navigate(`${paths.signIn}?returnUrl=${encodeURIComponent(returnUrl)}`);
    };

    const handleJoinCommunity = async (community: IPublicOrganiserCommunity) => {
        if (!community.isJoinable || community.isMember) {
            return;
        }

        if (!isLoggedIn) {
            redirectToSignIn();
            return;
        }

        try {
            setJoiningCommunityId(community.id);
            await participantCommunityHubService.joinCommunity(community.id);
            // Joining a community is a scoring action (network-score.md section 3) — let
            // LevelUpDialog re-check for a celebration immediately.
            dispatch(bumpScoreCheckVersion());

            const updateCommunity = (item: IPublicOrganiserCommunity) =>
                item.id === community.id
                    ? { ...item, isMember: true, isJoinable: false, memberCount: item.memberCount + 1 }
                    : item;

            setHostedCommunities((previous) => previous.map(updateCommunity));
            setParticipatingCommunities((previous) => previous.map(updateCommunity));

            notifications.show(`Joined ${community.name}`, { severity: 'success', autoHideDuration: 2200 });
            trackInteraction('JoinCommunity', community.name);
        } catch {
            notifications.show('Unable to join this community right now.', { severity: 'error', autoHideDuration: 2600 });
        } finally {
            setJoiningCommunityId(null);
        }
    };

    const handleFollow = async () => {
        if (!profile?.companyId) return;

        if (!isLoggedIn) {
            redirectToSignIn();
            return;
        }

        try {
            setFollowLoading(true);
            await eventService.followOrganiser(profile.companyId, 'OrganiserProfile');
            setIsFollowing(true);
            notifications.show('You are now following this organiser', { severity: 'success', autoHideDuration: 2200 });
            logEvent('Organiser Profile Page', 'click', 'Follow Organiser');
            trackInteraction('FollowClick', 'Follow Organiser');
        } catch (err: any) {
            notifications.show(err.message || 'Failed to follow organiser', { severity: 'error', autoHideDuration: 2500 });
        } finally {
            setFollowLoading(false);
        }
    };

    const handleUnfollow = async () => {
        if (!profile?.companyId) return;

        if (!isLoggedIn) {
            redirectToSignIn();
            return;
        }

        try {
            setFollowLoading(true);
            await eventService.unfollowOrganiser(profile.companyId);
            setIsFollowing(false);
            notifications.show('You have unfollowed this organiser', { severity: 'success', autoHideDuration: 2200 });
            logEvent('Organiser Profile Page', 'click', 'Unfollow Organiser');
            trackInteraction('UnfollowClick', 'Unfollow Organiser');
        } catch (err: any) {
            notifications.show(err.message || 'Failed to unfollow organiser', { severity: 'error', autoHideDuration: 2500 });
        } finally {
            setFollowLoading(false);
        }
    };

    const handleVisitWebsite = () => {
        logEvent('Organiser Profile Page', 'click', 'Visit Website');
        trackInteraction('WebsiteClick', 'Visit Website');
    };

    const handleSocialClick = (label: string) => {
        logEvent('Organiser Profile Page', 'click', `Social - ${label}`);
        trackInteraction('SocialClick', label);
    };

    const handleUpcomingEventClick = (title: string, eventId: string) => {
        logEvent('Organiser Profile Page', 'click', `Upcoming Event - ${title}`);
        trackInteraction('UpcomingEventClick', title, eventId);
    };

    const handlePastEventClick = (title: string, eventId: string) => {
        logEvent('Organiser Profile Page', 'click', `Past Event - ${title}`);
        trackInteraction('PastEventClick', title, eventId);
    };

    const handleViewAllCommunities = () => {
        setActiveTab('communities');
    };

    const handleOpenCommunity = (community: IPublicOrganiserCommunity) => {
        trackInteraction('CommunityClick', community.name);

        // communityDetails is a logged-in-only route - send anonymous visitors to sign in first
        // instead of letting ProtectedRoute silently bounce them back to the homepage.
        if (!isLoggedIn) {
            redirectToSignIn();
            return;
        }

        navigate(buildPath(paths.public.communityDetails, { slug: community.slug }));
    };

    const stats = profile
        ? [
            { label: 'Events hosted', value: profile.totalPublicEventsHosted },
            { label: 'Upcoming', value: profile.upcomingEventsCount },
            { label: 'Registrations', value: profile.totalRegistrations },
            { label: profile.yearsActive === 1 ? 'Year active' : 'Years active', value: profile.yearsActive },
        ]
        : [];

    const accentColour = profile?.primaryColour || theme.palette.primary.main;

    const websiteHost = getWebsiteHost(profile?.website);

    const heroMetaFragments = [
        profile?.industry,
        profile?.foundedYear ? `Founded ${profile.foundedYear}` : null,
    ].filter((fragment): fragment is string => !!fragment);

    const estimatedPastCount = profile
        ? Math.max(profile.totalPublicEventsHosted - profile.upcomingEventsCount, 0)
        : undefined;

    const tabItems: { value: OrganiserProfileTab; label: string; count?: number }[] = [
        { value: 'about', label: 'About' },
        {
            value: 'communities',
            label: 'Communities',
            count: communitiesLoaded ? communityCounts.hosted + communityCounts.participating : undefined,
        },
        { value: 'upcoming', label: 'Upcoming', count: upcomingLoaded ? upcomingTotalCount : profile?.upcomingEventsCount },
        { value: 'past', label: 'Past', count: pastLoaded ? pastTotalCount : estimatedPastCount },
    ];

    const orderedCommunities = [...hostedCommunities, ...participatingCommunities].sort((left, right) =>
        left.name.localeCompare(right.name, 'en-GB', { numeric: true, sensitivity: 'base' }),
    );
    const orderedHostedCommunities = [...hostedCommunities].sort((left, right) =>
        left.name.localeCompare(right.name, 'en-GB', { numeric: true, sensitivity: 'base' }),
    );
    const orderedParticipatingCommunities = [...participatingCommunities].sort((left, right) =>
        left.name.localeCompare(right.name, 'en-GB', { numeric: true, sensitivity: 'base' }),
    );
    const sidebarCommunities = orderedCommunities.slice(0, 4);
    const totalCommunityCount = communityCounts.hosted + communityCounts.participating;

    const socialLinks: SocialLink[] = [];
    if (profile?.linkedInUrl) socialLinks.push({ label: 'LinkedIn', href: profile.linkedInUrl, icon: <LinkedIn fontSize="small" /> });
    if (profile?.twitterUrl) socialLinks.push({ label: 'X', href: profile.twitterUrl, icon: <X fontSize="small" /> });
    if (profile?.instagramUrl) socialLinks.push({ label: 'Instagram', href: profile.instagramUrl, icon: <Instagram fontSize="small" /> });
    if (profile?.facebookUrl) socialLinks.push({ label: 'Facebook', href: profile.facebookUrl, icon: <Facebook fontSize="small" /> });

    const openEvent = (eventItem: IPublicOrganiserEventFeedItem, isPast: boolean) => {
        (isPast ? handlePastEventClick : handleUpcomingEventClick)(eventItem.title, eventItem.eventId);
        navigate(buildPath(paths.public.events.details, { id: eventItem.eventId }));
    };

    const renderEventRow = (eventItem: IPublicOrganiserEventFeedItem, isPast = false) => (
        <EventCard
            variant="row"
            key={eventItem.eventId}
            event={toEventRowItem(eventItem, isPast)}
            onClick={() => openEvent(eventItem, isPast)}
            categoryChip={null}
            statusChip={isPast ? (
                <Chip
                    label={`${(eventItem.registrationCount ?? 0).toLocaleString('en-GB')} registered`}
                    size="small"
                    sx={{ display: { xs: 'none', md: 'inline-flex' }, bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }}
                />
            ) : null}
        />
    );

    const organiserCanonicalPath = companyKey ? `/organisers/${companyKey}` : paths.public.eventOrganizers;
    const organiserTitle = profile
        ? `${profile.companyName.slice(0, 32)} - Event Organiser`
        : 'Event Organiser';
    const organiserDescription = profile
        ? (profile.aboutUs
            ? `${profile.companyName} organiser profile. ${profile.aboutUs.slice(0, 140)}`
            : `${profile.companyName} organiser profile with upcoming events on AttendIn.`)
        : 'Explore organiser profile details, communities, and upcoming events on AttendIn.';
    const organiserKeywords = profile
        ? `${profile.companyName}, event organiser, ${profile.companyName} events, AttendIn organiser`
        : 'event organiser, organiser profile, AttendIn organisers';

    return (
        <>
            <PageMetadata
                title={organiserTitle}
                description={organiserDescription}
                keywords={organiserKeywords}
                ogImage={profile?.logoUrl}
                canonicalUrl={organiserCanonicalPath}
            />
            {profile && (
                <OrganizationSchema
                    organization={{
                        name: profile.companyName,
                        description: profile.aboutUs || `${profile.companyName} organiser profile with upcoming events on AttendIn.`,
                        url: organiserCanonicalPath,
                        logo: profile.logoUrl,
                        sameAs: socialLinks.map((link) => link.href),
                    }}
                />
            )}
            <Box sx={{ minHeight: '100vh', position: 'relative', bgcolor: 'background.default', pb: { xs: 8, md: 12 } }}>
                {prefersReducedMotion ? (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                            opacity: 0.1,
                            pointerEvents: 'none',
                        }}
                    />
                ) : (
                    <AnimatedGradient
                        colors={[theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.main]}
                        duration={20}
                        opacity={0.1}
                    />
                )}
                <Container maxWidth={isLoggedIn ? false : 'lg'} sx={theme => isLoggedIn ? participantContentSx(theme) : { position: 'relative', zIndex: 1, pt: { xs: 10, md: 14 } }}>
                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 'var(--radius-panel)',
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                        }}
                    >
                        {loading ? (
                            <Box sx={{ p: { xs: 3, md: 4 } }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ md: 'center' }}>
                                    <Skeleton variant="rounded" width={96} height={96} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton height={44} width="45%" />
                                        <Skeleton height={24} width="60%" />
                                    </Box>
                                </Stack>
                                <Skeleton height={64} sx={{ mt: 3 }} />
                            </Box>
                        ) : (
                            <>
                                {profile?.bannerImageUrl ? (
                                    <Box
                                        component="img"
                                        src={profile.bannerImageUrl}
                                        alt=""
                                        loading="lazy"
                                        sx={{ width: '100%', height: { xs: 120, md: 180 }, objectFit: 'cover', display: 'block' }}
                                    />
                                ) : null}
                                <Box sx={{ p: { xs: 3, md: 4 } }}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ md: 'center' }}>
                                        <Avatar
                                            variant="rounded"
                                            src={profile?.logoUrl || undefined}
                                            alt={profile?.companyName}
                                            sx={{
                                                width: { xs: 64, md: 84 },
                                                height: { xs: 64, md: 84 },
                                                borderRadius: 'var(--radius-card)',
                                                bgcolor: alpha(accentColour, 0.12),
                                                color: accentColour,
                                                border: '1px solid',
                                                borderColor: alpha(accentColour, 0.24),
                                                fontSize: { xs: 24, md: 30 },
                                                fontWeight: 600,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {(profile?.companyName || '?').slice(0, 1).toUpperCase()}
                                        </Avatar>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Chip
                                                label="Organiser"
                                                size="small"
                                                sx={{
                                                    mb: 1,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 'var(--tracking-overline)',
                                                    bgcolor: 'var(--surface-chip)',
                                                    color: 'primary.dark',
                                                }}
                                            />
                                            <Typography variant="h4" component="h1" sx={{ wordBreak: 'break-word' }}>
                                                {profile?.companyName || 'Event organiser'}
                                            </Typography>
                                            {(heroMetaFragments.length > 0 || websiteHost) ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                                    {heroMetaFragments.join(' · ')}
                                                    {websiteHost ? (
                                                        <>
                                                            {heroMetaFragments.length > 0 ? ' · ' : ''}
                                                            <MuiLink
                                                                href={getExternalUrl(profile?.website)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={handleVisitWebsite}
                                                                underline="hover"
                                                            >
                                                                {websiteHost}
                                                            </MuiLink>
                                                        </>
                                                    ) : null}
                                                </Typography>
                                            ) : null}
                                        </Box>

                                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
                                            <Button
                                                variant="contained"
                                                onClick={isFollowing ? handleUnfollow : handleFollow}
                                                disabled={followLoading}
                                                startIcon={isFollowing ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                                            >
                                                {isFollowing ? 'Following' : 'Follow organiser'}
                                            </Button>
                                            <Button variant="outlined" onClick={handleCopyProfileLink} startIcon={<ContentCopy fontSize="small" />}>
                                                Copy link
                                            </Button>
                                        </Stack>
                                    </Stack>

                                    <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        useFlexGap
                                        columnGap={3}
                                        rowGap={1}
                                        sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}
                                    >
                                        {stats.map((stat) => (
                                            <Typography key={stat.label} variant="body2" color="text.secondary">
                                                <Typography component="span" variant="subtitle2" color="text.primary">
                                                    {stat.value.toLocaleString('en-GB')}
                                                </Typography>
                                                {' '}{stat.label.toLowerCase()}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </Paper>

                    <Stack
                        direction="row"
                        gap={1}
                        flexWrap="wrap"
                        useFlexGap
                        role="tablist"
                        aria-label="Organiser profile sections"
                        sx={{ my: 3 }}
                    >
                        {tabItems.map((tab) => {
                            const selected = activeTab === tab.value;
                            return (
                                <Chip
                                    key={tab.value}
                                    role="tab"
                                    aria-selected={selected}
                                    onClick={() => setActiveTab(tab.value)}
                                    label={
                                        tab.count !== undefined && tab.count > 0
                                            ? `${tab.label} · ${tab.count.toLocaleString('en-GB')}`
                                            : tab.label
                                    }
                                    variant={selected ? 'filled' : 'outlined'}
                                    sx={{
                                        height: 36,
                                        px: 0.5,
                                        fontWeight: 600,
                                        ...(selected
                                            ? {
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                            }
                                            : { bgcolor: 'background.paper' }),
                                    }}
                                />
                            );
                        })}
                    </Stack>

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            {activeTab === 'about' ? (
                                <Stack gap={3}>
                                    <Paper variant="outlined" sx={{ p: 3 }}>
                                        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                                            About
                                        </Typography>
                                        {loading ? (
                                            <>
                                                <Skeleton height={28} />
                                                <Skeleton height={28} />
                                                <Skeleton height={28} width="88%" />
                                            </>
                                        ) : (
                                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                                {profile?.aboutUs || 'This organiser has not added an about section yet.'}
                                            </Typography>
                                        )}

                                        {!loading && profile?.focusAreas?.length ? (
                                            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                                                {profile.focusAreas.map((focus) => (
                                                    <Chip
                                                        key={focus}
                                                        label={focus}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha(accentColour, 0.12),
                                                            color: accentColour,
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        ) : null}
                                    </Paper>

                                    <Paper variant="outlined" sx={{ p: 3 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                            <Typography variant="h6" component="h2">
                                                Upcoming events
                                            </Typography>
                                            {(profile?.upcomingEventsCount ?? 0) > 0 ? (
                                                <Button variant="text" size="small" onClick={() => setActiveTab('upcoming')}>
                                                    View all {profile?.upcomingEventsCount.toLocaleString('en-GB')}
                                                </Button>
                                            ) : null}
                                        </Stack>

                                        {loading ? (
                                            <Stack gap={1.5}>
                                                <Skeleton variant="rounded" height={88} />
                                                <Skeleton variant="rounded" height={88} />
                                            </Stack>
                                        ) : profile?.upcomingEvents?.length ? (
                                            <Stack gap={1.5}>
                                                {profile.upcomingEvents.map((eventItem) => renderEventRow(eventItem))}
                                            </Stack>
                                        ) : (
                                            <Stack alignItems="center" gap={1.5} sx={{ py: 6, textAlign: 'center' }}>
                                                <EventRounded sx={{ fontSize: 48, color: 'text.disabled' }} />
                                                <Typography variant="h6" color="text.secondary">
                                                    No upcoming events yet
                                                </Typography>
                                                <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 360 }}>
                                                    Follow this organiser and we'll let you know when the next event goes live.
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Paper>
                                </Stack>
                            ) : null}

                            {activeTab === 'communities' ? (
                                <Paper variant="outlined" sx={{ p: 3 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                        <Typography variant="h6" component="h2">
                                            Communities
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                                        Public communities are visible to everyone. Private and unlisted communities appear only when you are already a member.
                                    </Typography>

                                    {communitiesLoading ? (
                                        <Stack gap={1.5}>
                                            <Skeleton variant="rounded" height={132} />
                                            <Skeleton variant="rounded" height={132} />
                                        </Stack>
                                    ) : (hostedCommunities.length > 0 || participatingCommunities.length > 0) ? (
                                        <>
                                            <Stack gap={3}>
                                                {orderedHostedCommunities.length > 0 && (
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                                            Hosted communities
                                                        </Typography>
                                                        <Grid container spacing={1.5}>
                                                            {orderedHostedCommunities.map((community) => (
                                                                <Grid key={community.id} size={{ xs: 12, md: 6 }}>
                                                                    <CommunityCard
                                                                        dense
                                                                        community={toCommunityCardShape(community, profile?.companyName)}
                                                                        joining={joiningCommunityId === community.id}
                                                                        onJoin={community.isJoinable ? () => handleJoinCommunity(community) : undefined}
                                                                        onClick={() => handleOpenCommunity(community)}
                                                                    />
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                )}

                                                {orderedParticipatingCommunities.length > 0 && (
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                                            Also active in
                                                        </Typography>
                                                        <Grid container spacing={1.5}>
                                                            {orderedParticipatingCommunities.map((community) => (
                                                                <Grid key={community.id} size={{ xs: 12, md: 6 }}>
                                                                    <CommunityCard
                                                                        dense
                                                                        community={toCommunityCardShape(community, profile?.companyName)}
                                                                        joining={joiningCommunityId === community.id}
                                                                        onJoin={community.isJoinable ? () => handleJoinCommunity(community) : undefined}
                                                                        onClick={() => handleOpenCommunity(community)}
                                                                    />
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                )}
                                            </Stack>

                                            {(hasMoreCommunities || loadingMoreCommunities) ? (
                                                <Box ref={communitiesLoadMoreRef} sx={{ pt: 2, minHeight: 8 }}>
                                                    {loadingMoreCommunities ? (
                                                        <Grid container spacing={1.5}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Skeleton variant="rounded" height={132} />
                                                            </Grid>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Skeleton variant="rounded" height={132} />
                                                            </Grid>
                                                        </Grid>
                                                    ) : null}
                                                </Box>
                                            ) : null}
                                        </>
                                    ) : (
                                        <Stack alignItems="center" gap={1.5} sx={{ py: 6, textAlign: 'center' }}>
                                            <GroupsRounded sx={{ fontSize: 48, color: 'text.disabled' }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No communities to show yet
                                            </Typography>
                                            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 360 }}>
                                                This organiser hasn't made any communities public yet.
                                            </Typography>
                                            <Button
                                                component={RouterLink}
                                                to={`${paths.public.search}?type=communities`}
                                                variant="text"
                                                size="small"
                                            >
                                                Discover public communities
                                            </Button>
                                        </Stack>
                                    )}
                                </Paper>
                            ) : null}

                            {activeTab === 'upcoming' ? (
                                <Paper variant="outlined" sx={{ p: 3 }}>
                                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                                        Upcoming events
                                    </Typography>

                                    {upcomingLoading || loading ? (
                                        <Stack gap={1.5}>
                                            <Skeleton variant="rounded" height={88} />
                                            <Skeleton variant="rounded" height={88} />
                                        </Stack>
                                    ) : upcomingEvents.length ? (
                                        <Stack gap={1.5}>
                                            {upcomingEvents.map((eventItem) => renderEventRow(eventItem))}
                                        </Stack>
                                    ) : (
                                        <Stack alignItems="center" gap={1.5} sx={{ py: 6, textAlign: 'center' }}>
                                            <EventRounded sx={{ fontSize: 48, color: 'text.disabled' }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No upcoming events yet
                                            </Typography>
                                            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 360 }}>
                                                Follow this organiser and we'll let you know when the next event goes live.
                                            </Typography>
                                        </Stack>
                                    )}

                                    {(hasMoreEvents || loadingMoreEvents) ? (
                                        <Box ref={loadMoreRef} sx={{ pt: 1.5, minHeight: 8 }}>
                                            {loadingMoreEvents ? (
                                                <Stack gap={1.5}>
                                                    <Skeleton variant="rounded" height={88} />
                                                    <Skeleton variant="rounded" height={88} />
                                                </Stack>
                                            ) : null}
                                        </Box>
                                    ) : null}
                                </Paper>
                            ) : null}

                            {activeTab === 'past' ? (
                                <Paper variant="outlined" sx={{ p: 3 }}>
                                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                                        Past events
                                    </Typography>

                                    {pastLoading || loading ? (
                                        <Stack gap={1.5}>
                                            <Skeleton variant="rounded" height={88} />
                                            <Skeleton variant="rounded" height={88} />
                                        </Stack>
                                    ) : pastEvents.length ? (
                                        <Stack gap={1.5}>
                                            {pastEvents.map((eventItem) => renderEventRow(eventItem, true))}
                                        </Stack>
                                    ) : (
                                        <Stack alignItems="center" gap={1.5} sx={{ py: 6, textAlign: 'center' }}>
                                            <EventRounded sx={{ fontSize: 48, color: 'text.disabled' }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No past events yet
                                            </Typography>
                                            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 360 }}>
                                                Once this organiser has run public events, they'll appear here.
                                            </Typography>
                                        </Stack>
                                    )}

                                    {(hasMoreEvents || loadingMoreEvents) ? (
                                        <Box ref={loadMoreRef} sx={{ pt: 1.5, minHeight: 8 }}>
                                            {loadingMoreEvents ? (
                                                <Stack gap={1.5}>
                                                    <Skeleton variant="rounded" height={88} />
                                                    <Skeleton variant="rounded" height={88} />
                                                </Stack>
                                            ) : null}
                                        </Box>
                                    ) : null}
                                </Paper>
                            ) : null}
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Stack gap={3} sx={{ position: { md: 'sticky' }, top: { md: 96 }, maxHeight: { md: 'calc(100dvh - 96px)' }, overflowY: { md: 'auto' } }}>
                                <Paper variant="outlined" sx={{ p: 3 }}>
                                    <Typography variant="overline" component="h2" color="text.secondary">
                                        Details
                                    </Typography>

                                    <Stack gap={1.5} sx={{ mt: 1.5 }}>
                                        {profile?.industry ? (
                                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
                                                <Typography variant="body2" color="text.secondary">Industry</Typography>
                                                <Typography variant="subtitle2" sx={{ textAlign: 'right' }}>{profile.industry}</Typography>
                                            </Stack>
                                        ) : null}

                                        {profile?.foundedYear ? (
                                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
                                                <Typography variant="body2" color="text.secondary">Founded</Typography>
                                                <Typography variant="subtitle2" sx={{ textAlign: 'right' }}>{profile.foundedYear}</Typography>
                                            </Stack>
                                        ) : null}

                                        {profile?.nextEventDate ? (
                                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
                                                <Typography variant="body2" color="text.secondary">Next event</Typography>
                                                <Typography variant="subtitle2" sx={{ textAlign: 'right' }}>
                                                    {dayjs(profile.nextEventDate).format('D MMM YYYY')}
                                                </Typography>
                                            </Stack>
                                        ) : null}
                                    </Stack>

                                    {profile?.website ? (
                                        <Button
                                            href={getExternalUrl(profile.website) ?? profile.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={handleVisitWebsite}
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<Language fontSize="small" />}
                                            sx={{ mt: 2.5 }}
                                        >
                                            Visit website
                                        </Button>
                                    ) : null}

                                    {socialLinks.length ? (
                                        <Stack direction="row" gap={1} justifyContent="center" sx={{ mt: 2 }}>
                                            {socialLinks.map((social) => (
                                                <IconButton
                                                    key={social.label}
                                                    component="a"
                                                    href={social.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={social.label}
                                                    onClick={() => handleSocialClick(social.label)}
                                                    size="small"
                                                    sx={{ border: 1, borderColor: 'divider' }}
                                                >
                                                    {social.icon}
                                                </IconButton>
                                            ))}
                                        </Stack>
                                    ) : null}
                                </Paper>

                                {(communitiesLoading || sidebarCommunities.length > 0) ? (
                                    <Paper variant="outlined" sx={{ p: 3 }}>
                                        <Typography variant="overline" component="h2" color="text.secondary">
                                            Communities
                                        </Typography>

                                        {communitiesLoading ? (
                                            <Stack gap={0.75} sx={{ mt: 1.5 }}>
                                                <Skeleton variant="rounded" height={48} />
                                                <Skeleton variant="rounded" height={48} />
                                            </Stack>
                                        ) : (
                                            <Stack gap={0.75} sx={{ mt: 1.5 }}>
                                                {sidebarCommunities.map((community) => (
                                                    <CommunityCard
                                                        key={community.id}
                                                        variant="row"
                                                        borderless
                                                        community={toCommunityCardShape(community, profile?.companyName)}
                                                        onClick={() => handleOpenCommunity(community)}
                                                        action={
                                                            community.isMember ? (
                                                                <Chip label="Joined" size="small" color="success" sx={{ height: 22, fontSize: '0.68rem' }} />
                                                            ) : community.isJoinable ? (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    disabled={joiningCommunityId === community.id}
                                                                    onClick={() => handleJoinCommunity(community)}
                                                                    sx={{ borderRadius: 999, textTransform: 'none' }}
                                                                >
                                                                    {joiningCommunityId === community.id ? 'Joining…' : 'Join'}
                                                                </Button>
                                                            ) : undefined
                                                        }
                                                    />
                                                ))}
                                                {totalCommunityCount > sidebarCommunities.length ? (
                                                    <Button variant="text" size="small" onClick={handleViewAllCommunities} sx={{ alignSelf: 'flex-start' }}>
                                                        View all
                                                    </Button>
                                                ) : null}
                                            </Stack>
                                        )}
                                    </Paper>
                                ) : null}
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};

export default OrganiserProfilePage;
